import { FieldCacheService, NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, SetTimeouService } from "namirasoft-core";
import { CategoryRow, CategoryScopeRow } from "namirasoft-field";
import { EntryRow, UserFullRow } from "namirasoft-password";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBoxSearch, NSLoading, NSPanel, NSTable, StringFormatter, URLFormatter } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { Link } from "react-router-dom";
import { EntryLineFormatter } from "../formatters/EntryLineFormatter";
import { PasswordLayout } from "../PasswordLayout";
import { PasswordRouterProps } from "../PasswordRouterProps";
import { PasswordRoutes } from "../PasswordRoutes";
import Styles from "./HomePage.module.css";

export interface HomePageProps extends NSARouterMakerResult, PasswordRouterProps
{
    user: UserFullRow;
}

export interface HomePageState
{
    category_scopes: CategoryScopeRow[];
    categories: CategoryRow[];
    categories_loading: boolean;
    selected_category_id: string;
    search: string;
}

class HomePage extends Component<HomePageProps, HomePageState>
{
    private NSTable_Ref = createRef<NSTable<EntryRow>>();
    private search_timeout = new SetTimeouService();

    private CATEGORY_ID_ALL = "all";

    constructor(props: HomePageProps)
    {
        super(props);
        this.state = {
            category_scopes: [],
            categories: [],
            categories_loading: true,
            selected_category_id: this.CATEGORY_ID_ALL,
            search: "",
        };
    }

    override componentDidMount()
    {
        this.loadCategories();
    }

    async loadCategories()
    {
        this.setState({ categories_loading: true });
        let cache = FieldCacheService.get(
            this.props.account.token_manager,
            this.props.product_id,
            this.props.notifier.onError,
        );
        let rows = await cache.get();
        this.setState({
            category_scopes: [...rows.category_scopes].sort((a, b) => a.name.localeCompare(b.name)),
            categories: [...rows.categories].sort((a, b) => a.name.localeCompare(b.name)),
            categories_loading: false,
        });
    }

    onSearchChanged(value: string)
    {
        this.search_timeout.setTimeoutIfNotCalledAgain(() =>
        {
            this.setState(
                () => ({ search: value }),
                () => this.NSTable_Ref.current?.reload(null, null));
        }, 1000);
    }

    onCategoryClicked(category_id: string)
    {
        this.setState(
            () => ({ selected_category_id: category_id }),
            () => this.NSTable_Ref.current?.reload(null, null)
        );
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <PasswordLayout
                {...this.props}
                showDownloadExtension
                action={{
                    title: "Home",
                    description: "Search your vault, browse categories on the left, and view entries on the right."
                }}
            >
                <div className={Styles.ns_home_root}>
                    <div className={Styles.ns_home_body}>
                        <NSPanel classList={[Styles.ns_home_groups_panel]}>
                            <div className={Styles.ns_home_group_title}>
                                <span>Categories</span>
                            </div>
                            <div
                                onClick={() => this.onCategoryClicked(this.CATEGORY_ID_ALL)}
                                className={`${Styles.ns_home_group_item} ${this.state.selected_category_id === this.CATEGORY_ID_ALL ? Styles.ns_home_group_item_selected : ""}`}
                            >
                                All
                            </div>
                            {
                                this.state.categories_loading
                                    ?
                                    <NSLoading small hideTitle hideDescription />
                                    :
                                    this.state.category_scopes.length > 0
                                        ? this.state.category_scopes.map(scope =>
                                        {
                                            let scope_categories = this.state.categories.filter(category => category.category_scope_id === scope.id);
                                            return (
                                                <div key={scope.id}>
                                                    <div className={Styles.ns_home_scope_title}>
                                                        {scope.name}
                                                    </div>
                                                    {
                                                        scope_categories.length > 0
                                                            ? scope_categories.map(category => (
                                                                <div
                                                                    key={category.id}
                                                                    onClick={() => this.onCategoryClicked(category.id)}
                                                                    className={`${Styles.ns_home_group_item} ${Styles.ns_home_category_item} ${this.state.selected_category_id === category.id ? Styles.ns_home_group_item_selected : ""}`}
                                                                >
                                                                    {category.name}
                                                                </div>
                                                            ))
                                                            : (
                                                                <div className={Styles.ns_home_group_empty}>
                                                                    No categories in this scope.
                                                                </div>
                                                            )
                                                    }
                                                </div>
                                            );
                                        })
                                        :
                                        (
                                            <div className={Styles.ns_home_group_empty}>
                                                You haven't added any category yet.
                                            </div>
                                        )
                            }
                        </NSPanel>

                        <NSPanel classList={[Styles.ns_home_table_panel]}>
                            <NSTable<EntryRow>
                                ref={this.NSTable_Ref}
                                name="home_entries"
                                checkbox={false}
                                columns={[
                                    { table: { name: "entry", text: "Entry" }, index: 0, type: "String", name: "name", text: "Title", formatter: new StringFormatter() },
                                    { table: { name: "entry", text: "Entry" }, index: 1, type: "String", name: "domain", text: "Domain", formatter: new StringFormatter() },
                                    { table: { name: "entry", text: "Entry" }, index: 2, type: "String", name: "url", text: "URL", formatter: new URLFormatter() },
                                    { table: { name: "entry", text: "Entry" }, index: 3, type: "String", name: "entry_line", text: "Lines", formatter: new EntryLineFormatter(this.props, "320px") },
                                    { table: { name: "entry", text: "Entry" }, index: 4, type: "String", name: "description", text: "Description", formatter: new StringFormatter() },
                                ]}
                                getRowKey={(row) => row.value.id}
                                getCellFormattedValue={(value, column, row, printable) =>
                                {
                                    if (column.name === "name")
                                        return <Link to={PasswordRoutes.EntryView(row.value.id)}>{row.value.name}</Link>;
                                    return column.formatter.format(value, column, row, printable);
                                }}
                                getRows={async (page, size) =>
                                {
                                    let filters: FilterItem[] = [];

                                    if (this.state.selected_category_id !== this.CATEGORY_ID_ALL)
                                        filters.push(
                                            new FilterItem(
                                                namirasoft.password.tables.entry_category,
                                                namirasoft.password.tables.entry_category.columns.category_id,
                                                false,
                                                FilterItemOperator.all.equals,
                                                this.state.selected_category_id
                                            )
                                        );

                                    if (this.state.search)
                                        filters.push(
                                            new FilterItem(
                                                namirasoft.password.tables.entry,
                                                namirasoft.password.tables.entry.columns.name,
                                                false,
                                                FilterItemOperator.all.contains,
                                                this.state.search
                                            )
                                        );
                                    return await this.props.servers.password.entry.List(filters, page, size, [], null);
                                }}
                                headerChildren={
                                    <NSBoxSearch
                                        classList={[Styles.ns_home_search]}
                                        required={false}
                                        title=""
                                        hideHeader
                                        placeholder="Search entries by name"
                                        autoComplete="off"
                                        onChanged={(e) => this.onSearchChanged(e.getValue(false) ?? "")}
                                    />
                                }
                                classList={[Styles.ns_home_table]}
                            />
                        </NSPanel>
                    </div>
                </div>
            </PasswordLayout>
        );
    }
}

export default HomePage;